// src/encounter/lib/reason-for-visit.ts
// Reason-for-Visit / Start-action mapping for the pre-encounter rail.
// Source: pre_encounter_widget.dart:415-465 — the primary action's label is driven
// by the encounter status (Initial → Start, Draft → Continue, Completed → Edit,
// Error → Retry). The status→label mapping is the only logic; rendering is the DS Button.

import type { EncounterStatus } from '@/types/encounter-api';

/** The four primary-action variants the Flutter Start button cycles through. */
export type StartAction = 'start' | 'continue' | 'edit' | 'retry';

/**
 * Map an encounter status to the pre-encounter primary action.
 * - no encounter yet / Initial → start
 * - Draft (in progress) → continue
 * - Completed / Signed → edit
 * - Error → retry
 * Mirrors startEncounter() in patient_encounter_bloc.dart:2555.
 */
export function startActionFor(status: EncounterStatus | undefined): StartAction {
  switch (status) {
    case 'Draft':
      return 'continue';
    case 'Completed':
    case 'Signed':
      return 'edit';
    case 'Error':
      return 'retry';
    case 'Initial':
    default:
      return 'start';
  }
}

/** Appointment modality (Flutter appointment.location.name → locationIcon). */
export type VisitModality = 'office' | 'home' | 'virtual';

/**
 * Map an appointment/encounter location name to a modality glyph key
 * (Flutter appointment_model.dart `locationIcon`: Office→office, Home→home,
 * Virtual→virtual, else block/none). Case-insensitive; unknown → undefined so
 * the RFV modality glyph simply hides (matches Flutter's ScripsIcons.block gap).
 */
export function locationModality(locationName: string | null | undefined): VisitModality | undefined {
  switch ((locationName ?? '').trim().toLowerCase()) {
    case 'office':
      return 'office';
    case 'home':
      return 'home';
    case 'virtual':
      return 'virtual';
    default:
      return undefined;
  }
}

/**
 * Map an appointment-type code to the RFV visit-type chip.
 * Wire codes (Flutter `app_constants.dart:691-692`): `ROUTINE` = first-time,
 * `FOLLOWUP` = follow-up. Flutter's own test is `code == appointmentTypeFollowUp`, so every
 * NON-follow-up code falls to the first-time glyph — reproduced here rather than
 * allow-listing `ROUTINE`, so a tenant-configured third type doesn't silently blank the chip.
 * Absent/blank code → undefined, and the chip hides (nothing to assert).
 */
export function visitTypeFor(
  appointmentTypeCode: string | null | undefined,
): 'follow-up' | 'first' | undefined {
  const code = (appointmentTypeCode ?? '').trim();
  if (!code) return undefined;
  return code.toUpperCase() === 'FOLLOWUP' ? 'follow-up' : 'first';
}

/** Human label for the primary action button. */
export function startActionLabel(action: StartAction): string {
  switch (action) {
    case 'continue':
      return 'Continue';
    case 'edit':
      return 'Edit';
    case 'retry':
      return 'Retry';
    case 'start':
    default:
      return 'Start';
  }
}
